import type { ReactNode } from 'react';

export type ActivityAction =
  | 'task.created'
  | 'task.updated'
  | 'task.status_changed'
  | 'task.assigned'
  | 'task.completed'
  | 'task.deleted'
  | 'project.created'
  | 'project.archived'
  | 'note.created'
  | 'note.deleted'
  | 'comment.created'
  | 'comment.deleted'
  | 'calendar_event.created'
  | 'calendar_event.updated'
  | 'calendar_event.deleted'
  | 'member.joined'
  | 'member.left'
  | (string & {}); // tolerate unknown action codes from forward-compat

export interface ActivityEntryAttrs {
  workspace_id?: string;
  actor_user_id?: string;
  actor_name?: string | null;
  actor_email?: string | null;
  subject_type?: string;
  subject_id?: string;
  action?: ActivityAction;
  metadata?: Record<string, unknown>;
  created_at?: string | null;
}

export interface ActivityEntry {
  id: string;
  attributes?: ActivityEntryAttrs;
}

export interface FormattedActivity {
  /** A short verb phrase — caller composes "<actor> {verb}". */
  verb: string;
  /** Optional inline subject reference (typically clickable). */
  subject: ActivitySubjectRef | null;
  /** Optional secondary clause shown after the subject (e.g. "from To-do to In progress"). */
  detail?: ReactNode;
}

export interface ActivitySubjectRef {
  label: string;
  href:
    | { kind: 'task'; taskUlid: string }
    | { kind: 'note'; noteUlid: string }
    | { kind: 'project'; projectSlug: string }
    | { kind: 'none' };
}

const meta = (entry: ActivityEntry): Record<string, unknown> =>
  entry.attributes?.metadata ?? {};

const stringFromMeta = (entry: ActivityEntry, key: string): string | null => {
  const v = meta(entry)[key];
  return typeof v === 'string' ? v : null;
};

/**
 * Translate an activity-log row's `action` + `metadata` into a
 * verb-phrase + subject-ref shape the `<ActivityFeed>` component
 * renders. Snapshotted titles / status names live in `metadata`,
 * so we don't need live lookups for the entity the feed is about
 * (which may have been deleted in the meantime).
 *
 * Anything we don't recognize falls back to a generic "did
 * something" verb — the activity feed is forward-compatible with
 * future event codes.
 */
export const formatActivity = (entry: ActivityEntry): FormattedActivity => {
  const action = entry.attributes?.action ?? '';
  const subjectType = entry.attributes?.subject_type;
  const subjectId = entry.attributes?.subject_id;
  const m = meta(entry);

  switch (action) {
    case 'task.created':
      return {
        verb: 'created task',
        subject: taskSubject(subjectId, stringFromMeta(entry, 'title')),
      };

    case 'task.updated': {
      const fields = Array.isArray(m.fields) ? (m.fields as string[]) : [];
      const fieldList = fields.length > 0 ? ` (${fields.join(', ')})` : '';
      return {
        verb: 'updated',
        subject: taskSubject(subjectId, stringFromMeta(entry, 'title')),
        detail: fieldList,
      };
    }

    case 'task.status_changed':
      return {
        verb: 'moved',
        subject: taskSubject(subjectId, stringFromMeta(entry, 'title')),
        detail: ` from ${stringFromMeta(entry, 'from_status_name') ?? '—'} to ${stringFromMeta(entry, 'to_status_name') ?? '—'}`,
      };

    case 'task.assigned': {
      const toName = stringFromMeta(entry, 'to_user_name');
      const verb = toName ? `assigned ${toName} to` : 'unassigned';
      return {
        verb,
        subject: taskSubject(subjectId, stringFromMeta(entry, 'title')),
      };
    }

    case 'task.completed':
      return {
        verb: 'completed',
        subject: taskSubject(subjectId, stringFromMeta(entry, 'title')),
      };

    case 'task.deleted':
      return {
        verb: 'deleted task',
        subject: { label: stringFromMeta(entry, 'title') ?? 'a task', href: { kind: 'none' } },
      };

    case 'project.created':
      return {
        verb: 'created project',
        subject: projectSubject(stringFromMeta(entry, 'slug'), stringFromMeta(entry, 'name')),
      };

    case 'project.archived':
      return {
        verb: 'archived project',
        subject: projectSubject(stringFromMeta(entry, 'slug'), stringFromMeta(entry, 'name')),
      };

    case 'note.created':
      return {
        verb: 'wrote note',
        subject: noteSubject(subjectId, stringFromMeta(entry, 'title')),
        detail: noteParentDetail(entry),
      };

    case 'note.deleted':
      return {
        verb: 'deleted note',
        subject: { label: stringFromMeta(entry, 'title') ?? 'a note', href: { kind: 'none' } },
      };

    case 'comment.created': {
      const onWhat = subjectType === 'task'
        ? taskSubject(subjectId, stringFromMeta(entry, 'task_title'))
        : noteSubject(subjectId, stringFromMeta(entry, 'note_title'));
      return { verb: 'commented on', subject: onWhat };
    }

    case 'comment.deleted': {
      const onWhat = subjectType === 'task'
        ? taskSubject(subjectId, stringFromMeta(entry, 'task_title'))
        : noteSubject(subjectId, stringFromMeta(entry, 'note_title'));
      return { verb: "removed a comment from", subject: onWhat };
    }

    case 'member.joined':
      return { verb: 'joined the workspace', subject: null };

    case 'member.left':
      return { verb: 'left the workspace', subject: null };

    case 'calendar_event.created':
      return {
        verb: 'scheduled event',
        // Events don't have a dedicated detail route (they open in
        // the calendar dialog), so subject is non-clickable for now.
        subject: { label: stringFromMeta(entry, 'title') ?? 'an event', href: { kind: 'none' } },
      };

    case 'calendar_event.updated': {
      const fields = Array.isArray(m.fields) ? (m.fields as string[]) : [];
      const fieldList = fields.length > 0 ? ` (${fields.join(', ')})` : '';
      return {
        verb: 'updated event',
        subject: { label: stringFromMeta(entry, 'title') ?? 'an event', href: { kind: 'none' } },
        detail: fieldList,
      };
    }

    case 'calendar_event.deleted':
      return {
        verb: 'cancelled event',
        subject: { label: stringFromMeta(entry, 'title') ?? 'an event', href: { kind: 'none' } },
      };

    default:
      return { verb: 'did something', subject: null };
  }
};

const taskSubject = (id: string | undefined, title: string | null): ActivitySubjectRef => ({
  label: title ?? 'a task',
  href: id ? { kind: 'task', taskUlid: id } : { kind: 'none' },
});

const noteSubject = (id: string | undefined, title: string | null): ActivitySubjectRef => ({
  label: title ?? 'a note',
  href: id ? { kind: 'note', noteUlid: id } : { kind: 'none' },
});

const projectSubject = (
  slug: string | null,
  name: string | null,
): ActivitySubjectRef => ({
  label: name ?? 'a project',
  href: slug ? { kind: 'project', projectSlug: slug } : { kind: 'none' },
});

const noteParentDetail = (entry: ActivityEntry): string => {
  const parentType = stringFromMeta(entry, 'parent_type');
  if (parentType === 'project') {
    const name = stringFromMeta(entry, 'project_name');
    return name ? ` on ${name}` : '';
  }
  if (parentType === 'task') {
    const title = stringFromMeta(entry, 'task_title');
    return title ? ` on ${title}` : '';
  }
  return '';
};
